import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {Subject, takeUntil, throttleTime} from 'rxjs';
import {Job, JobGroup, JobFrontendState, JOB_STATUS, JOB_TYPE, JOB_TYPES, RtJobGroup, RtJobState} from 'src/app/models/Job';
import {User} from 'src/app/models/User';
import {JobService} from 'src/app/services/job.service';
import {SnackbarService} from 'src/app/services/snackbar.service';

export interface PdfRegenerationJobsDialogData {
  projectId: string;
  user: User;
  initialRtJobStates?: Array<RtJobState | Partial<RtJobGroup> & {id?: string}>;
  initialState?: JobFrontendState;
}

@Component({
  selector: 'app-pdf-regeneration-jobs',
  templateUrl: './pdf-regeneration-jobs.component.html',
  styleUrls: ['./pdf-regeneration-jobs.component.css']
})
export class PdfRegenerationJobsComponent implements OnInit, OnDestroy {
  protected readonly JOB_STATUS = JOB_STATUS;
  jobGroups: JobGroup[] = [];
  selectedJobGroupId: string;
  selectedJobGroup: JobGroup | undefined;
  private monotonicGroupProgress = 0;
  private monotonicGroupProgressId: string | undefined;
  jobs: Job[] = [];
  isDeleting = false;
  private rtPdfRegenerationStates: Array<RtJobState | Partial<RtJobGroup>> = [];
  private ngUnsubscribe = new Subject<void>();
  private jobsUnsubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<PdfRegenerationJobsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfRegenerationJobsDialogData,
    private jobservice: JobService,
    private snackbarservice: SnackbarService,
  ) {
    this.rtPdfRegenerationStates = (data.initialRtJobStates || []) as any;
    const rtGroup = this.rtPdfRegenerationStates.find(s =>
      (s as RtJobGroup).totalCount !== undefined
    ) as (RtJobGroup & {id?: string}) | undefined;
    if (rtGroup) {
      const uuid = (rtGroup.id || '').replace(/^GROUP_/, '');
      const groupId = `${JOB_TYPES.JOB_GROUP}#${data.projectId}#${JOB_TYPE.PDF_REGENERATION}#${uuid}`;
      this.selectedJobGroupId = groupId;
      this.selectedJobGroup = {
        id: groupId,
        projectId: data.projectId,
        type: JOB_TYPES.JOB_GROUP,
        jobType: rtGroup.jobType,
        totalCount: rtGroup.totalCount,
        jobCount: rtGroup.jobCount,
        createdAt: rtGroup.createdAt,
        user: rtGroup.user,
      } as JobGroup;
      this.jobGroups = [this.selectedJobGroup];
    }
  }

  ngOnInit(): void {
    this.jobservice.rtDbGetJobStatesForProject(this.data.projectId, 'pdfRegeneration')
      .pipe(
        // Same flood mitigation as project page: regen burst can fire hundreds of RTDB events
        // per second; collapse to at most 2/sec so progress UI doesn't churn.
        throttleTime(500, undefined, {leading: true, trailing: true}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(rtJobStates => {
        this.rtPdfRegenerationStates = rtJobStates || [];
      });

    if (this.selectedJobGroupId) {
      this.selectJobGroup(this.selectedJobGroupId);
    }

    this.jobservice.getPdfRegenerationJobGroupsForProject(this.data.projectId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(jobGroups => {
        this.jobGroups = jobGroups;
        if (jobGroups.length === 0) {
          this.selectedJobGroup = undefined;
          return;
        }
        const matched = this.selectedJobGroupId
          ? jobGroups.find(jobGroup => jobGroup.id === this.selectedJobGroupId)
          : undefined;
        if (matched) {
          this.selectedJobGroup = matched;
        } else {
          this.selectJobGroup(jobGroups[0].id);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.jobsUnsubscribe.next();
    this.jobsUnsubscribe.complete();
  }

  selectJobGroup(jobGroupId: string): void {
    this.selectedJobGroupId = jobGroupId;
    this.selectedJobGroup = this.jobGroups.find(jobGroup => jobGroup.id === jobGroupId);
    this.jobsUnsubscribe.next();
    this.jobservice.getJobsForJobGroup(jobGroupId)
      .pipe(takeUntil(this.jobsUnsubscribe), takeUntil(this.ngUnsubscribe))
      .subscribe(jobs => {
        this.jobs = jobs.sort((a, b) => (a.jobIdentifier || a.id).localeCompare(b.jobIdentifier || b.id));
      });
  }

  timestamp(value: any): string {
    if (!value) {
      return '';
    }
    const date = typeof value.toDate === 'function' ? value.toDate() : value;
    return date instanceof Date ? date.toLocaleString() : '';
  }

  groupLabel(jobGroup: JobGroup): string {
    const status = jobGroup.state?.status || 'QUEUED';
    return `${this.timestamp(jobGroup.createdAt)} - ${status} - ${jobGroup.totalCount || 0} PDF-er`;
  }

  displayJobGroupStatus(): JOB_STATUS | string {
    const status = this.selectedRtJobGroup()?.status
      || this.aggregatedChildStatus()
      || this.selectedJobGroup?.state?.status
      || JOB_STATUS.QUEUED;
    return status === JOB_STATUS.COMPLETED && this.displayJobGroupProgress() < 100
      ? JOB_STATUS.IN_PROGRESS
      : status;
  }

  displayJobGroupProgress(): number {
    const raw = this.rawGroupProgress();
    const groupId = this.selectedJobGroup?.id || (this.selectedRtJobGroup() as any)?.id;
    if (groupId && groupId !== this.monotonicGroupProgressId) {
      this.monotonicGroupProgressId = groupId;
      this.monotonicGroupProgress = 0;
    }
    this.monotonicGroupProgress = Math.max(this.monotonicGroupProgress, raw);
    return this.monotonicGroupProgress;
  }

  private rawGroupProgress(): number {
    const rtJobGroup = this.selectedRtJobGroup();
    if (rtJobGroup?.progress !== undefined) {
      return rtJobGroup.progress;
    }
    const childStates = this.childRtJobStates();
    const divisor = rtJobGroup?.jobCount || childStates.length;
    if (divisor > 0) {
      const sum = childStates.reduce((acc, s) => acc + (s.progress || 0), 0);
      return Math.round(sum / divisor);
    }
    return this.selectedJobGroup?.state?.progress ?? 0;
  }

  private childRtJobStates(): RtJobState[] {
    return this.rtPdfRegenerationStates.filter(s =>
      (s as RtJobGroup).totalCount === undefined
    ) as RtJobState[];
  }

  private aggregatedChildStatus(): JOB_STATUS | undefined {
    const childStates = this.childRtJobStates();
    if (childStates.length === 0) {
      return undefined;
    }
    if (childStates.some(s => s.status === JOB_STATUS.FAILED)) {
      return JOB_STATUS.FAILED;
    }
    if (childStates.every(s => s.status === JOB_STATUS.COMPLETED)) {
      return JOB_STATUS.COMPLETED;
    }
    if (childStates.some(s => s.status === JOB_STATUS.SAVING_RTDB)) {
      return JOB_STATUS.SAVING_RTDB;
    }
    if (childStates.some(s => s.status === JOB_STATUS.SAVING_FIRESTORE)) {
      return JOB_STATUS.SAVING_FIRESTORE;
    }
    if (childStates.some(s => s.status === JOB_STATUS.IN_PROGRESS)) {
      return JOB_STATUS.IN_PROGRESS;
    }
    return JOB_STATUS.QUEUED;
  }

  displayJobStatus(job: Job): JOB_STATUS {
    const rtJobState = this.rtJobStateForJob(job);
    const rtJobGroup = this.selectedRtJobGroup();
    if (rtJobState?.status) {
      return rtJobState.status;
    }
    if (rtJobGroup?.status === JOB_STATUS.COMPLETED && (rtJobGroup.progress ?? 0) >= 100) {
      return JOB_STATUS.COMPLETED;
    }
    return job.state?.status;
  }

  displayJobProgress(job: Job): number {
    const rtJobState = this.rtJobStateForJob(job);
    const rtJobGroup = this.selectedRtJobGroup();
    if (rtJobState?.progress !== undefined) {
      return rtJobState.progress;
    }
    if (rtJobGroup?.status === JOB_STATUS.COMPLETED && (rtJobGroup.progress ?? 0) >= 100) {
      return 100;
    }
    return job.state?.progress || 0;
  }

  private selectedRtJobGroup(): Partial<RtJobGroup> {
    return this.rtPdfRegenerationStates.find(rtJobState =>
      (rtJobState as RtJobGroup).totalCount !== undefined
    ) as Partial<RtJobGroup>;
  }

  private rtJobStateForJob(job: Job): RtJobState {
    return this.rtPdfRegenerationStates.find(rtJobState =>
      rtJobState.id === job.jobIdentifier && (rtJobState as RtJobGroup).totalCount === undefined
    ) as RtJobState;
  }

  async deleteSelectedJobGroup(): Promise<void> {
    if (!this.selectedJobGroup || !this.data.user?.superAdmin || this.isDeleting) {
      return;
    }
    this.isDeleting = true;
    try {
      await this.jobservice.deleteJobGroup(this.selectedJobGroup.id);
      this.selectedJobGroupId = null;
      this.jobs = [];
      this.snackbarservice.openSnackBar('Jobb slettet.', 'OK');
    } catch (error: any) {
      this.snackbarservice.openSnackBar(error?.message || 'Kunne ikke slette jobb.', 'OK');
    } finally {
      this.isDeleting = false;
    }
  }
}
